"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.NORDIC_CALM_THEME = void 0;
const common_1 = require("./common");
exports.NORDIC_CALM_THEME = (0, common_1.firstPartyTheme)({
    id: 'nordic-calm',
    name: 'Nordic Calm',
    tagline: 'Minimal Scandinavian: chalk white, spruce green and oak, quiet enough to hear yourself think.',
    description: 'Nordic Calm is a minimal Scandinavian system for studios, clinics, architects, furniture makers, cafés, SaaS that wants to feel humane, and any business whose promise is clarity. Chalk and stone neutrals, a deep spruce primary, a warm oak accent, Manrope headings over DM Sans text, soft 6px corners and an airy rhythm. Includes a matching dark palette for evening reading.',
    categories: ['minimal', 'local-business'],
    tokens: {
        colors: {
            background: '#f7f7f4',
            surface: '#eeeee9',
            text: '#22262a',
            muted: '#6e747a',
            primary: '#2f4a3f',
            primaryText: '#f7f7f4',
            accent: '#c9a27e',
            border: '#deded7',
        },
        darkColors: {
            background: '#161a1d',
            surface: '#1f2428',
            text: '#e8e8e3',
            muted: '#9aa1a7',
            primary: '#a8c3b4',
            primaryText: '#12201a',
            accent: '#d3b08c',
            border: '#2c3237',
        },
        fonts: { heading: 'Manrope', body: 'DM Sans' },
        radius: 6,
        density: 'airy',
        typeScale: 1.2,
    },
    guide: `## Mood
A bright room in Copenhagen in February: pale light, a wooden table, one plant, nothing unnecessary. Calm, honest, practical. The design gets out of the way so the content feels considered. Lagom: not too little, not too much.

## Grid and layout
- A centred 1120px container with a consistent 12-column grid. Content blocks align to the same left edge down the page; that shared edge is the main design device.
- Whitespace is structure. Use the section spacing token between every section and never compress it to fit more in.
- Prefer two-column splits (text and image, 5/7 or 6/6) and simple three-up feature rows. No more than three items in a row, ever.
- Cards are rare. When needed, use the surface colour with no shadow and a 1px border, 6px radius.

## Typography
- Manrope for headings, weight 600, sentence case, modest sizes: the scale is gentle (1.2), so hierarchy comes from weight and space, not size jumps. Hero headline at the 4xl step at most.
- DM Sans for body at 16–18px, line-height 1.65, measure 60–70 characters.
- Small labels in DM Sans weight 500, muted colour, no uppercase tracking gimmicks.

## Colour
Chalk and stone carry the page. Spruce green is the only strong colour: primary buttons, links, the active state. Oak is warmth in small doses: an icon fill, a highlighted number, a thin top border on a featured card. Do not introduce extra hues; use photos for colour.

## Imagery
Natural daylight, muted palettes, real materials: wood grain, wool, ceramics, concrete, plants. Wide shots with negative space, people seen in context rather than posing. Slightly cool white balance. Images use the theme radius and never have borders or shadows. Prefer one large image over a collage.

## Motion
Almost none, and purposeful. Opacity fades of 300–400ms with ease-out; hover states change colour, not size. Respect prefers-reduced-motion fully. No marquees, no parallax, no scroll-jacking.

## Copy tone
Plain, warm, specific. Short sentences, active voice, no superlatives ("the best", "revolutionary"). Say what it is and who it is for, then show it. Headlines are statements of fact or calm promises: "Furniture made to be lived with", "A clinic that listens first".

## Do
- Keep navigation to the essentials, with a single spruce CTA on the right.
- Use real opening hours, addresses, materials and names: specificity is the luxury here.
- Support dark mode properly when enabled; the dark palette is designed for it.

## Don't
- No gradients, glow, glassmorphism, or neon.
- No icon-heavy feature grids with six identical cards.
- No stock photos of people pointing at laptops.`,
    sections: [
        {
            name: 'Hero: quiet statement',
            recipe: 'Container-width section with a 6/6 split. Left: a small muted label, a Manrope headline (4xl step, weight 600, max two lines), one paragraph of body copy, a spruce primary button and a text link. Right: one large photograph with the theme radius, aspect 4:5. Vertical padding uses the section spacing token. No background colour change.',
        },
        {
            name: 'Three principles',
            recipe: 'A three-column row separated by 1px vertical border lines (no cards). Each column: a small oak-coloured number (01, 02, 03), a short Manrope heading (lg step) and two sentences of body copy. Stacks to one column on mobile with horizontal rules instead.',
        },
        {
            name: 'Services list',
            recipe: 'A definition-list style layout: each row has the service name on the left (Manrope, xl), a one-line description in the middle, and a price or duration on the right in muted text, rows divided by 1px borders with generous padding. A single spruce button below the list.',
        },
        {
            name: 'Visit us',
            recipe: 'Split section on the surface colour: left has address, opening hours as a two-column table, phone and email; right has a static map image or a photo of the storefront with the theme radius. Ends with a "Book a visit" primary button.',
        },
    ],
});
